import React from 'react';
import keys from '../../../constants/hammerstoneConstantKeys';
import { ActivityPriority, MAX_BACKFILL_COUNT, ScheduleType } from '../../../constants/hammerstoneConstants';
import {
	BackfillInput,
	couldBackfill,
	getBackfillCountThresholds,
	getBackfillsByType,
	hasUserEditedSchedule,
	objToOptions,
} from 'src/commons';
import Activity, { ActivityContainerProps, ActivityFieldGroupProps } from '../../../interfaces/activityInterfaces';
import Content, { Rules } from 'src/components/helpers/content/';
import { Alert, Button, ColumnLayout, Container, Header, SpaceBetween } from '@amzn/awsui-components-console';
import { useActivity } from 'src/data/redux';
import { useFormContext } from 'react-hook-form';
import { BackfillExecutionsAlert, NextExecutions, ColumnContainer } from 'src/components/helpers';
import { DisplayMode } from 'src/components/helpers/content/contentInterfaces';

const { NEW, SCHEDULED, EDIT_PENDING } = keys.StatusCode;

const PriorityOptions = objToOptions(ActivityPriority);
const ScheduleTypeOptions = objToOptions(ScheduleType);

/**
 * A dedicated diff view for the Schedule container.
 * All fields (scheduleDate, scheduleType, scheduleInterval) come directly from the API response.
 */
function DiffScheduleContainer(props: ActivityContainerProps) {
	const contentProps = {
		resourceId: props.activityId,
		diffResourceId: props.diffActivityId,
		resourceType: 'activity',
		mode: DisplayMode.Diff,
	} as const;

	return (
		<ColumnContainer header={props.header} columns={2}>
			<Alert type="info">
				Schedule date tracking in revision history was introduced in Q2 2026. Revisions created before this
				feature was enabled do not include schedule date information.
			</Alert>
			<Content.Datetime {...contentProps} label="Schedule date" path="scheduleDate" />
			<Content.Text {...contentProps} label="Schedule type" path="scheduleType" />
			<Content.Text {...contentProps} label="Schedule interval" path="scheduleInterval" />
		</ColumnContainer>
	);
}

/**
 * An editable container for information about an Activity's Schedule
 */
export default function ScheduleContainer(props: ActivityContainerProps) {
	const contentProps = {
		mode: props.mode,
		resourceType: 'activity',
		resourceId: props.activityId,
	} as const;

	// Ensure activity data is fetched before any conditional rendering
	const { data: activity } = useActivity(props.activityId);

	if (props.mode === DisplayMode.Diff) {
		return <DiffScheduleContainer {...props} />;
	}

	return (
		<Container
			data-testid={`column-container:schedule`}
			header={<Header variant="h2">{props.header}</Header>}
			footer={<RetryFields {...props} />}
		>
			<SpaceBetween size="m">
				<ScheduleFields {...props} />
				<Content.Select
					{...contentProps}
					label="Priority"
					path="activityPriority"
					options={PriorityOptions}
					rules={(l) => ({ required: Rules.required(l) })}
					disableOn={{
						paths: ['activityStatusCode'],
						condition: (activity) =>
							// Priority should NOT be disabled when creating an Activity, in which case the the activityStatus will not be defined
							activity?.activityStatusCode &&
							![NEW, SCHEDULED, EDIT_PENDING].includes(activity?.activityStatusCode),
					}}
				/>
			</SpaceBetween>
		</Container>
	);
}

export function RetryFields(props: ActivityFieldGroupProps) {
	const contentProps = {
		mode: props.mode,
		resourceType: 'activity',
		resourceId: props.activityId,
		diffResourceId: props.diffActivityId,
		disabled: props.disabled,
	} as const;

	return (
		<Content.Toggle {...contentProps} label="Retry on failure" path="activityRetryActive">
			<ColumnLayout columns={2} variant="text-grid">
				<SpaceBetween size="s" direction="horizontal">
					<Content.Number
						{...contentProps}
						label="Maximum retries"
						path="activityRetryMaximum"
						disableOn={{
							paths: ['activityRetryActive'],
							condition: (activity) => !activity.activityRetryActive,
						}}
						rules={(l) => ({
							required: Rules.required(l),
							min: Rules.min(1, l),
							max: Rules.max(99, l),
							validate: { int: Rules.isInteger(l) },
						})}
					/>
					<Content.Number
						{...contentProps}
						label="Delay between retries (mins)"
						path="activityRetryDelay"
						disableOn={{
							paths: ['activityRetryActive'],
							condition: (activity) => !activity.activityRetryActive,
						}}
						rules={(l) => ({
							required: Rules.required(l),
							min: Rules.min(1, l),
							max: Rules.max(60, l),
							validate: { int: Rules.isInteger(l) },
						})}
					/>
				</SpaceBetween>
				<SpaceBetween size="m">
					<Content.Toggle
						{...contentProps}
						label="Pause activity if all retries fail"
						path="activityRetryPause"
						disableOn={{
							paths: ['activityRetryActive'],
							condition: (activity) => !activity.activityRetryActive,
						}}
					/>
				</SpaceBetween>
			</ColumnLayout>
		</Content.Toggle>
	);
}

const SCHEDULE_DEPENDENT_FIELDS = ['scheduleDate', 'scheduleType', 'scheduleInterval'] as const;
export function ScheduleFields(props: ActivityFieldGroupProps) {
	const contentProps = {
		mode: props.mode,
		resourceType: 'activity',
		resourceId: props.activityId,
		diffResourceId: props.diffActivityId,
		disabled: props.disabled,
	} as const;

	const { data: activity } = useActivity(props.activityId);
	const form = useFormContext<Activity>();
	// form.watch() tracks changes to field values: https://react-hook-form.com/docs/useform/watch
	const [scheduleDate, scheduleType, scheduleInterval] = form?.watch(SCHEDULE_DEPENDENT_FIELDS) ?? [];

	const backfillProps = {
		...props,
		viewValues: (activity ?? {}) as BackfillInput,
		editValues: { scheduleDate, scheduleType, scheduleInterval },
		djsLastExecutionDate: activity?.djsLastExecutionDate,
		activityStatus: activity?.activityStatusCode,
		isScheduleChanged: activity?.scheduleChanged,
	};
	const backfillsByType = getBackfillsByType(backfillProps);
	const { isBackfillExcessive } = getBackfillCountThresholds(backfillsByType?.EDIT_SCHEDULE?.count);

	return (
		<>
			<BackfillExecutionsAlert {...backfillProps} />
			<ColumnLayout columns={2} variant="text-grid">
				<SpaceBetween size="m">
					<Content.Datetime
						{...contentProps}
						label="Schedule date"
						path="scheduleDate"
						editDescription={
							<>
								All datetimes are displayed in UTC. <i>Note: The scheduler ignores seconds.</i>
								{props.editAction === 'edit' &&
									activity?.scheduleDate &&
									hasUserEditedSchedule(backfillProps) && (
										<>
											To revert to the current schedule (undo changes),{' '}
											<Button
												variant="inline-link"
												data-testid="resetSchedule"
												disabled={props.disabled || !activity}
												onClick={(e) => {
													e.preventDefault();
													SCHEDULE_DEPENDENT_FIELDS.forEach((f) => form.resetField(f));
													form.trigger();
												}}
											>
												click here.
											</Button>
										</>
									)}
							</>
						}
						rules={(l) => ({
							required: Rules.required(l),
							pattern: Rules.pattern.completeDatetime(l),
							validate: {
								backfillCount: () =>
									!couldBackfill(backfillProps) ||
									!isBackfillExcessive ||
									`Cannot backfill more than ${MAX_BACKFILL_COUNT} executions, please update the schedule date.`,
							},
						})}
						nowButton={backfillsByType?.EDIT_SCHEDULE?.scheduleUnit || 'minute'}
					/>
					{props.mode !== DisplayMode.Loading && <NextExecutions {...backfillProps} />}
				</SpaceBetween>
				<SpaceBetween size="m">
					<SpaceBetween size="s" direction="horizontal">
						<Content.Number
							{...contentProps}
							label="Every"
							path="scheduleInterval"
							rules={(l) => ({
								required: Rules.required(l),
								min: Rules.min(1, l),
								max: Rules.max(999, l),
								validate: { int: Rules.isInteger(l) },
							})}
							disableOn={{
								paths: ['scheduleType'],
								condition: (activity) => activity.scheduleType === keys.ScheduleType.ONETIME,
							}}
						/>
						<Content.Select
							{...contentProps}
							label="Interval"
							path="scheduleType"
							options={ScheduleTypeOptions}
							rules={(l) => ({ required: Rules.required(l) })}
							onChange={(event, setField, setValue) => {
								// Get value from event emitted by Select
								const { value } = event.detail.selectedOption;
								// Set the value of the current field to the selected option
								setField(value);
								if (value === keys.ScheduleType.ONETIME) {
									// If setting the schedule type to only run once, overwrite the scheduleInterval to 1
									setValue('scheduleInterval', 1);
								}
							}}
						/>
					</SpaceBetween>
				</SpaceBetween>
			</ColumnLayout>
		</>
	);
}
